import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { BreadcrumbService, IBreadcrumb } from '@app/shared/services/breadcrumb.service';
import { CommunicatorService } from '@app/shared/services/communicator.service';
import { FeatureService } from '@app/shared/services/feature.service';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

/** Component creates breadcrumb links */
@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.scss'],
    standalone: false
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
  public dynamicParamData: { [key: string]: string };
  public breadcrumb$: Observable<IBreadcrumb[]>;
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    public communicator: CommunicatorService,
    private readonly translate: TranslateService,
    private readonly feature: FeatureService
  ) {
    this.observeRouteChange();
  }

  public ngOnInit(): void {
    this.breadcrumb$ = this.breadcrumbService.breadcrumb.asObservable();
    this.observeBreadcrumbLabelChanges();

    debug('breadcrumbs', 'init');
    debug(
      'breadcrumb',
      'iframeService check - app ',
      this.communicator.appRunningInIframe ? 'is iframed' : 'is NOT iframed'
    );
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private observeRouteChange(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          let output = route;
          while (output.firstChild) {
            output = output.firstChild;
          }

          return output;
        }),
        filter((route) => route.outlet === PRIMARY_OUTLET),
        takeUntil(this.destroy$)
      )
      .subscribe(this.processRouteBreadcrumb.bind(this));
  }

  private processRouteBreadcrumb(route: ActivatedRoute): void {
    this.dynamicParamData = {};
    this.breadcrumbService.reset();

    let routeBreadcrumbData = this.fetchRouteBreadcrumbData(route);
    if (routeBreadcrumbData) {
      const breadcrumb: IBreadcrumb[] = JSON.parse(JSON.stringify(routeBreadcrumbData));
      this.parseBreadcrumbRouteParams(breadcrumb, route).forEach((crumb) => {
        this.breadcrumbService.add(crumb);
      });
    } else {
      this.breadcrumbService.reset();
    }
  }

  /**
   * Replace any crumb.url or crumb.label route-based parameters.
   */
  private parseBreadcrumbRouteParams(
    breadcrumb: IBreadcrumb[],
    route: ActivatedRoute
  ): IBreadcrumb[] {
    return breadcrumb.map((crumb) => {
      // Split route my/special/detail/page/:routeParamId
      const urlChunks = crumb.url.split('/');

      // Replace any :routeParamIds in crumb.url with route value
      // e.g. my/special/detail/page/12345
      for (const chunk of urlChunks) {
        if (chunk.includes(':')) {
          const paramID = chunk.replace(':', '');
          const routerParamID = route.snapshot.params[paramID];
          crumb.url = crumb.url.replace(`:${paramID}`, routerParamID);
        }
      }

      // Get crumb label translation if it exists
      let translatedCrumb = this.translate.instant(crumb.label);
      debug('translate', `translated crumb label "${crumb.label}" -> "${translatedCrumb}"`);
      crumb.label = translatedCrumb;

      // Replace any {{routeParamId}} in crumb.label with route value
      // e.g. My Special {{routeParamId}}
      crumb.labelWithTemplateVars = crumb.label; // store raw label with {{variables}} for later use
      const labelParams = crumb.label.match(/[^{{]+(?=\}})/g);
      if (labelParams) {
        for (const labelParam of labelParams) {
          const routerParamID = route.snapshot.params[labelParam.trim()];
          if (routerParamID) {
            crumb.label = crumb.label.replace(`{{${labelParam}}}`, routerParamID);
            crumb.labelWithTemplateVars = crumb.label; // render route-based variables into the template label
          }
        }
      }

      return crumb;
    });
  }

  private fetchRouteBreadcrumbData(route: ActivatedRoute): any {
    return route.snapshot.data.breadcrumb;
  }

  /**
   * Subscribe to changes in breadcrumbService.breadcrumbLabels and update crumb.label {{variables}}
   */
  private observeBreadcrumbLabelChanges(): void {
    combineLatest([
      this.breadcrumbService.breadcrumbLabels,
      this.feature.hasFeature('adminUI.v3.IFC.migration')
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([labelData, hasIfc]) => {
        // Store the label values

        for (const label of Object.keys(labelData)) {
          if (labelData.hasOwnProperty(label)) {
            this.dynamicParamData[label] = labelData[label]; // store most recent label value
          }
        }

        // Update breadcrumbs from un-rendered crumb.labelWithTemplateVars string containing template {{variables}}
        let newCrumbs = this.breadcrumbService.get();
        newCrumbs.map((crumb) => {
          const labelParams = crumb.labelWithTemplateVars.match(/[^{{]+(?=\}})/g);
          if (labelParams) {
            for (const labelParam of labelParams) {
              const dynamicData = this.dynamicParamData[labelParam]; // pull from stored value
              if (dynamicData) {
                crumb.label = crumb.labelWithTemplateVars.replace(
                  new RegExp(`{{${labelParam}}}`, 'g'),
                  dynamicData
                );
              }
            }
          }
        });

        this.breadcrumbService.reset(newCrumbs);
        if (this.communicator.appRunningInIframe) {
          // Ignore top two breadcrumb levels, parent should fetch from Admin-V1 menu.yml
          let subCrumbs = newCrumbs.slice(2);

          // Broadcast breadcrumb changes to parent - Legacy
          // In IFC this is handled on route change
          if (!hasIfc) {
            this.communicator.breadcrumbChanged(subCrumbs);
          }
        }
      });
  }
}
